import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useDebounce, useSearch } from "@/hooks/useSearch";
import { MovieGrid } from "@/components/movie/MovieRow";
import { SearchSkeleton } from "@/components/skeleton/Skeletons";
import { EmptyState, ErrorState } from "@/components/common/States";
import { Page } from "@/components/common/Reveal";
import { FilterSheet, SORT_OPTIONS } from "@/components/search/FilterSheet";
import { IconSearch, IconSliders, IconXmark } from "@/components/icons/Icons";
import { Chip } from "@/components/glass/Glass";
import { useCategories } from "@/hooks/useMovies";
import { useSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

const clean = (s) => (s || "").replace(/[<>]/g, "").slice(0, 100);

export function SearchBar({ value, onChange, onClear, onFilter, filterCount = 0, autoFocus = true }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const [focus, setFocus] = useState(false);
  useEffect(() => {
    if (autoFocus) ref.current?.focus({ preventScroll: true });
  }, [autoFocus]);
  return (
    <motion.div
      initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.94, y: -6 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.2, 0.9, 0.2, 1] }}
      className="flex items-center gap-2.5"
    >
      <label className={cn("pk-glass flex h-14 min-w-0 flex-1 items-center gap-3 rounded-[20px] px-4 transition-[background-color,box-shadow] duration-300 sm:h-[60px] sm:px-5", focus && "bg-white/[.09] shadow-[0_0_0_4px_rgba(255,255,255,.06)]")}>
        <IconSearch size={21} className="shrink-0 text-white/55" />
        <span className="sr-only">Qidiruv</span>
        <input
          ref={ref}
          type="search"
          inputMode="search"
          enterKeyHint="search"
          autoComplete="off"
          spellCheck={false}
          value={value}
          onChange={(e) => onChange(clean(e.target.value))}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          placeholder="Kinoni izlash..."
          className="h-full min-w-0 flex-1 bg-transparent text-[17px] font-medium text-white placeholder:text-white/40 focus:outline-none [&::-webkit-search-cancel-button]:hidden"
          data-testid="search-input"
          aria-label="Kinoni izlash"
        />
        <AnimatePresence>
          {value && (
            <motion.button initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} onClick={onClear} aria-label="Tozalash" className="grid h-7 w-7 place-items-center rounded-full bg-white/15 text-white" data-testid="search-clear-button">
              <IconXmark size={14} strokeWidth={2.2} />
            </motion.button>
          )}
        </AnimatePresence>
      </label>
      <AnimatePresence>
        {focus && value && (
          <motion.button
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: "auto" }}
            exit={{ opacity: 0, width: 0 }}
            transition={{ type: "spring", stiffness: 420, damping: 36 }}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              onClear();
              ref.current?.blur();
            }}
            className="overflow-hidden whitespace-nowrap text-[16px] font-semibold text-white/80 sm:hidden"
            data-testid="search-cancel-button"
          >
            Bekor qilish
          </motion.button>
        )}
      </AnimatePresence>
      <button onClick={onFilter} aria-label="Filtrlar" className={cn("pk-glass relative h-14 w-14 shrink-0", focus && value ? "hidden sm:grid" : "grid", " place-items-center rounded-[20px] text-white transition-colors hover:bg-white/[.1] sm:h-[60px] sm:w-[60px]")} data-testid="search-filter-button">
        <IconSliders size={22} />
        {filterCount > 0 && <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-white px-1 text-[11px] font-bold text-black">{filterCount}</span>}
      </button>
    </motion.div>
  );
}

export default function Search() {
  const [params, setParams] = useSearchParams();
  const [text, setText] = useState(() => clean(params.get("q")));
  const [filters, setFilters] = useState(() => ({ genre: params.get("genre") || "", type: params.get("type") || "", sort: params.get("sort") || "popular" }));
  const [sheet, setSheet] = useState(false);
  const q = useDebounce(text, 300);
  const { data: cats } = useCategories();

  // keep URL in sync (replace, not push)
  useEffect(() => {
    const next = {};
    if (q) next.q = q;
    if (filters.genre) next.genre = filters.genre;
    if (filters.type) next.type = filters.type;
    if (filters.sort && filters.sort !== "popular") next.sort = filters.sort;
    setParams(next, { replace: true });
  }, [q, filters, setParams]);

  useSeo({ title: q ? `“${q}” — qidiruv` : "Qidiruv" });

  const { data, isLoading, isError, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isPlaceholderData } = useSearch(q, filters);
  const items = useMemo(() => {
    const seen = new Set();
    return (data?.pages || []).flatMap((p) => p.items).filter((m) => (seen.has(m.id) ? false : seen.add(m.id)));
  }, [data]);
  const total = data?.pages?.[0]?.total ?? 0;
  const typing = text.trim() !== q.trim();
  const filterCount = (filters.genre ? 1 : 0) + (filters.type ? 1 : 0) + (filters.sort !== "popular" ? 1 : 0);
  const sortLabel = SORT_OPTIONS.find((s) => s.value === filters.sort)?.label;

  return (
    <Page className="pk-pb-dock pt-[calc(76px+var(--safe-top))] lg:pt-28">
      <div className="pk-container">
        <div className="mx-auto max-w-3xl">
          <SearchBar value={text} onChange={setText} onClear={() => setText("")} onFilter={() => setSheet(true)} filterCount={filterCount} />
          <div className="pk-scroll-x -mx-4 mt-4 flex gap-2 overflow-x-auto px-4 sm:mx-0 sm:flex-wrap sm:px-0">
            <Chip active={!filters.genre} onClick={() => setFilters((f) => ({ ...f, genre: "" }))} data-testid="search-genre-all">
              Barchasi
            </Chip>
            {(cats?.items || []).filter((g) => g.count > 0).map((g) => (
              <Chip key={g.slug} active={filters.genre === g.slug} onClick={() => setFilters((f) => ({ ...f, genre: f.genre === g.slug ? "" : g.slug }))} data-testid={`search-genre-${g.slug}`}>
                {g.name}
              </Chip>
            ))}
          </div>
        </div>

        <div className="mt-8 flex items-baseline justify-between gap-3">
          <h1 className="text-[22px] font-bold tracking-[-0.015em] text-white sm:text-[26px]" data-testid="search-heading">
            {q ? "Natijalar" : "Ommabop filmlar"}
          </h1>
          {!isLoading && !isError && (
            <span className="text-[13.5px] font-medium text-white/50" data-testid="search-count">
              {total} ta film{sortLabel && filters.sort !== "popular" ? ` · ${sortLabel}` : ""}
            </span>
          )}
        </div>

        <div className={cn("mt-5 transition-opacity duration-300", (typing || isPlaceholderData) && items.length > 0 && "opacity-60")} data-testid="search-results">
          {isLoading ? (
            <SearchSkeleton />
          ) : isError ? (
            <ErrorState error={error} onRetry={refetch} />
          ) : !items.length ? (
            <EmptyState icon={IconSearch} title="Film topilmadi" text={q ? `“${q}” bo‘yicha hech narsa topilmadi. Boshqa so‘z bilan urinib ko‘ring.` : "Hozircha filmlar yo‘q."} testId="search-empty-state" />
          ) : (
            <MovieGrid items={items} hasMore={Boolean(hasNextPage)} loadMore={fetchNextPage} loadingMore={isFetchingNextPage} testId="search-grid" />
          )}
        </div>
      </div>
      <FilterSheet open={sheet} onOpenChange={setSheet} value={filters} onChange={setFilters} onReset={() => setFilters({ genre: "", type: "", sort: "popular" })} />
    </Page>
  );
}
